/**
 * Hook libellés publics FR/EN — BT-028 / BT-028-R3.
 */
import { useI18n } from "@/coquille-bridge";

const PUBLIC_COPY = {
  fr: {
    home: {
      kicker: "SHOTOKAN AAMC — BLACK DRAGON",
      title: "Découvrez le karaté — discipline, respect, progression",
      lead: "Rejoignez une communauté martiale structurée : cours pour enfants, ados et adultes, encadrement par des Sensei expérimentés, progression par ceintures.",
      ctaBecomeMember: "Devenir membre",
      ctaAccount: "Demander un compte",
      ctaDojos: "Voir les dojos",
      ctaLogin: "Connexion",
      checklistCourses: "Cours structurés et progressifs",
      checklistAges: "Enfants, ados et adultes",
      checklistSensei: "Encadrement par des Sensei expérimentés",
      heroMessage:
        "Que vous cherchiez confiance, condition physique ou art martial traditionnel — nos dojos vous accueillent dans un cadre respectueux.",
      whyTitle: "Pourquoi pratiquer le karaté ?",
      audienceTitle: "Pour enfants, ados et adultes",
      audienceLead: "Des groupes adaptés à chaque âge — contactez un dojo pour les horaires.",
      dojosTitle: "Nos dojos",
      dojosDesc: "Trouvez un dojo près de chez vous.",
      dojosLink: "Explorer les dojos",
      instructorsTitle: "Nos instructeurs",
      instructorsDesc: "Sensei et titres martiaux — leadership Shotokan AAMC.",
      instructorsLink: "Voir les instructeurs",
      structureTitle: "Structure organisationnelle",
      structureLead: "Organisation Shotokan AAMC.",
      portalStatusNote: "Bienvenue sur le portail Shotokan AAMC.",
      coverageTitle: "Ce que le portail couvre",
      laterTitle: "Fonctions à venir",
      laterBadge: "Bientôt",
      demoPathTitle: "Parcours rapide",
      demoDuration: "Durée",
      whyCards: [
        { id: "discipline", icon: "🎯", title: "Discipline", desc: "Structure, régularité et objectifs clairs à chaque séance." },
        { id: "respect", icon: "🤝", title: "Respect", desc: "Valeurs martiales, courtoisie et esprit d'équipe au dojo." },
        { id: "progression", icon: "📈", title: "Progression", desc: "Parcours par ceintures, examens et accompagnement personnalisé." },
      ],
      audienceCards: [
        { id: "kids", title: "Enfants", desc: "Coordination, confiance et respect des règles.", status: "Dès 6 ans" },
        { id: "teens", title: "Ados", desc: "Technique, condition et préparation aux examens.", status: "Groupes dédiés" },
        { id: "adults", title: "Adultes", desc: "Art martial complet, forme et dépassement.", status: "Tous niveaux" },
      ],
    },
    about: {
      title: "À propos",
      subtitle: "Shotokan AAMC — Fraternité Black Dragon of the Ancient Warriors.",
    },
    directory: {
      cards: "Cartes",
      table: "Tableau",
      dojosTitle: "Dojos",
      dojosSubtitle: "Répertoire des dojos — emplacements [DÉMO] en attente des 10 dojos officiels.",
      instructorsTitle: "Instructeurs",
      instructorsSubtitle: "Leadership reçu — associations dojo à confirmer [DÉMO].",
      studentsTitle: "Étudiants",
      studentsSubtitle: "Annuaire lecture seule — données [DÉMO].",
      emptyDojos: "Aucun dojo trouvé — essayez Réinitialiser si un filtre est actif.",
      emptyStudents:
        "Aucun étudiant ne correspond aux filtres — essayez Réinitialiser.",
      emptyStudentsFiltered: "Aucun étudiant ne correspond — essayez Réinitialiser.",
      emptyInstructors:
        "Aucun instructeur trouvé — leadership reçu, associations dojo à confirmer avec le mandat client.",
      dojosOfficialWait: (official, demo) =>
        `Les 10 dojos officiels sont en attente du fichier client rempli (KARATE_MODELES_IMPORT_PHASE1.xlsx). Actuellement : ${official} officiel(s), ${demo} [DÉMO].`,
      studentsOfficialWait:
        "Jeu initial minimal — noms fournis (Marcel Charette, Natalie Lavigne) ; grades et groupes « à confirmer ». Import manuel ou massif possible ultérieurement. L'enfant [DÉMO] sert uniquement à tester l'espace Parent.",
      martialTitlesTitle: "Titres martiaux (aperçu)",
      columns: {
        name: "Nom",
        city: "Ville",
        address: "Adresse",
        students: "Étudiants",
        type: "Type",
        status: "Statut",
        title: "Titre",
        email: "Courriel",
        group: "Groupe",
        belt: "Ceinture",
        reference: "Référence",
        visibility: "Visibilité",
        category: "Catégorie",
      },
      typeOfficial: "Officiel",
      typeReceived: "Reçu",
      unassigned: "Non assigné",
      viewDetails: "Voir détail",
      directions: "Itinéraire",
      leadershipReceived: "Leadership reçu — association dojo à confirmer",
      senseiNoDojo: "Sensei sans dojo assigné",
    },
    documents: {
      title: "Documents",
      subtitle: "Public, membres, admin ou à valider — rien n'est publié automatiquement sans validation.",
      warning:
        "Les documents Black Dragon, protocoles, grades et titres ne doivent pas être publiés publiquement sans validation officielle du Hanshi.",
      legend: "Public · Réservé membres · Privé admin · À valider (Hanshi)",
      pendingTitle: "À valider avant publication",
      classifyAdmin: "Classifier dans l'admin",
      emptyGuest: "Connectez-vous pour voir les documents réservés aux membres. Les visiteurs voient seulement le contenu public validé.",
      emptyMember:
        "Aucun document visible pour votre profil — contenu [DÉMO] ou en attente de validation Hanshi.",
      emptyFiltered: "Aucun document ne correspond — essayez Réinitialiser.",
    },
    protocols: {
      title: "Protocoles / grades / titres",
      subtitle: "Mode lecture prudente — publication complète soumise à validation Hanshi.",
      warning:
        "Contenu sensible : grades, titres et protocoles Black Dragon restent en visibilité contrôlée. Ne pas diffuser publiquement sans accord du Hanshi.",
      structureTitle: "Grades et titres (structure)",
      emptyGuest: "Connectez-vous pour accéder aux protocoles réservés aux membres.",
      emptyMember:
        "Aucun protocole publié pour le moment — contenu sensible en validation Hanshi. Consultez aussi la section Documents.",
    },
    privacy: {
      title: "Politique de confidentialité",
      subtitle: "Plateforme de démonstration Phase 1 — aucune donnée réelle traitée pour l'instant.",
      metaDescription:
        "Politique de confidentialité minimale — portail Shotokan AAMC Phase 1 en mode démonstration.",
      contactLink: "Contact",
      footerNote:
        "Version actuelle : plateforme de démonstration et de préparation. Les pratiques définitives seront précisées avant toute mise en production avec données réelles.",
      sections: [
        {
          id: "demo",
          heading: "Données de démonstration",
          bullets: [
            "Les données affichées sont marquées [DÉMO] ou proviennent de documents reçus en attente de validation.",
            "Aucune donnée personnelle réelle n'est importée automatiquement en Phase 1.",
          ],
        },
        {
          id: "no-real",
          heading: "Ce que le système ne fait pas encore",
          bullets: [
            "Aucun paiement n'est géré.",
            "Aucun document réel n'est stocké sur un serveur sécurisé.",
            "Aucun backend d'authentification réelle n'est actif.",
            "Aucune vente ni partage de données à des tiers.",
          ],
        },
        {
          id: "future",
          heading: "Utilisation future",
          bullets: [
            "Des comptes utilisateurs pourront être créés en Phase 2, avec consentement et règles à définir.",
            "Les coordonnées officielles de contact seront confirmées par l'organisation.",
          ],
        },
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "Informations et demandes — mode Phase 1 / démonstration.",
      metaDescription: "Contact Shotokan AAMC — formulaire de démonstration, coordonnées à confirmer.",
      intro:
        "Cette page permet de comprendre comment demander de l'information. Les coordonnées officielles de l'organisation sont encore à confirmer.",
      emailLabel: "Courriel indicatif :",
      demoEmail: "contact [DÉMO]@shotokan-aamc.demo",
      phase1Note:
        "La demande de compte et le contact sont en mode Phase 1 : aucun envoi réel vers un serveur, aucun stockage de message.",
      formNotice: "Formulaire visuel de simulation — aucune donnée n'est envoyée ni enregistrée.",
      nameLabel: "Nom",
      emailFieldLabel: "Courriel",
      messageLabel: "Message",
      submitLabel: "Simuler l'envoi",
      simulationMessage:
        "[DÉMO] Message simulé localement seulement — aucun envoi réel, aucun stockage. Utilisez la demande de compte pour tester le parcours Phase 1.",
      accountRequestLink: "Demande de compte",
      privacyLink: "Politique de confidentialité",
    },
    dojoDetail: {
      notFound: "Dojo introuvable",
      notFoundMessage: "Ce dojo n'existe pas ou a été retiré.",
      backToList: "← Retour à la liste des dojos",
      subtitleDemo: "Fiche dojo — emplacement [DÉMO]",
      subtitleOfficial: "Fiche dojo — donnée officielle",
      infoTitle: "Informations",
      id: "Identifiant",
      phone: "Téléphone",
      activeStudents: "Étudiants actifs",
      attachedStudents: "Étudiants rattachés",
      noStudents: "Aucun étudiant [DÉMO] ou officiel pour ce dojo.",
      unassigned: "Non assigné",
    },
  },
  en: {
    home: {
      kicker: "SHOTOKAN AAMC — BLACK DRAGON",
      title: "Discover karate — discipline, respect, progress",
      lead: "Join a structured martial community: classes for children, teens and adults, guidance from experienced Sensei, progression through belts.",
      ctaBecomeMember: "Become a member",
      ctaAccount: "Request an account",
      ctaDojos: "View dojos",
      ctaLogin: "Log in",
      checklistCourses: "Structured progressive classes",
      checklistAges: "Children, teens and adults",
      checklistSensei: "Guidance from experienced Sensei",
      heroMessage:
        "Whether you seek confidence, fitness or traditional martial arts — our dojos welcome you in a respectful setting.",
      whyTitle: "Why practice karate?",
      audienceTitle: "For children, teens and adults",
      audienceLead: "Age-appropriate groups — contact a dojo for schedules.",
      dojosTitle: "Our dojos",
      dojosDesc: "Find a dojo near you.",
      dojosLink: "Explore dojos",
      instructorsTitle: "Our instructors",
      instructorsDesc: "Sensei and martial titles — Shotokan AAMC leadership.",
      instructorsLink: "View instructors",
      structureTitle: "Organizational structure",
      structureLead: "Shotokan AAMC organization.",
      portalStatusNote: "Welcome to the Shotokan AAMC portal.",
      coverageTitle: "What the portal covers",
      laterTitle: "Coming later",
      laterBadge: "Soon",
      demoPathTitle: "Quick path",
      demoDuration: "Duration",
      whyCards: [
        { id: "discipline", icon: "🎯", title: "Discipline", desc: "Structure, consistency and clear goals at every class." },
        { id: "respect", icon: "🤝", title: "Respect", desc: "Martial values, courtesy and team spirit at the dojo." },
        { id: "progression", icon: "📈", title: "Progress", desc: "Belt pathway, exams and personalized guidance." },
      ],
      audienceCards: [
        { id: "kids", title: "Children", desc: "Coordination, confidence and respect for rules.", status: "From age 6" },
        { id: "teens", title: "Teens", desc: "Technique, conditioning and exam preparation.", status: "Dedicated groups" },
        { id: "adults", title: "Adults", desc: "Complete martial art, fitness and personal growth.", status: "All levels" },
      ],
    },
    about: {
      title: "About",
      subtitle: "Shotokan AAMC — Black Dragon Brotherhood of the Ancient Warriors.",
    },
    directory: {
      cards: "Cards",
      table: "Table",
      dojosTitle: "Dojos",
      dojosSubtitle: "Dojo directory — [DEMO] slots until 10 official dojos are loaded.",
      instructorsTitle: "Instructors",
      instructorsSubtitle: "Leadership received — dojo links to confirm [DEMO].",
      studentsTitle: "Students",
      studentsSubtitle: "Read-only directory — [DEMO] data.",
      emptyDojos: "No dojo found — try Reset if a filter is active.",
      emptyStudents:
        "No students match the filters — try Reset.",
      emptyStudentsFiltered: "No student matches — try Reset.",
      emptyInstructors:
        "No instructor found — leadership received, dojo links to confirm with the client mandate.",
      dojosOfficialWait: (official, demo) =>
        `The 10 official dojos are waiting for the completed client file (KARATE_MODELES_IMPORT_PHASE1.xlsx). Currently: ${official} official, ${demo} [DEMO].`,
      studentsOfficialWait:
        "Minimal initial dataset — provided names (Marcel Charette, Natalie Lavigne); belts and groups marked to confirm. Manual or bulk import possible later. The [DEMO] child is only for testing the Parent space.",
      martialTitlesTitle: "Martial titles overview",
      columns: {
        name: "Name",
        city: "City",
        address: "Address",
        students: "Students",
        type: "Type",
        status: "Status",
        title: "Title",
        email: "Email",
        group: "Group",
        belt: "Belt",
        reference: "Reference",
        visibility: "Visibility",
        category: "Category",
      },
      typeOfficial: "Official",
      typeReceived: "Received",
      unassigned: "Unassigned",
      viewDetails: "View details",
      directions: "Directions",
      leadershipReceived: "Leadership received — dojo link to confirm",
      senseiNoDojo: "Sensei without assigned dojo",
    },
    documents: {
      title: "Documents",
      subtitle: "Public, members, admin or to validate — nothing is published automatically without validation.",
      warning:
        "Black Dragon documents, protocols, grades and titles must not be published publicly without official Hanshi validation.",
      legend: "Public · Members only · Admin private · To validate (Hanshi)",
      pendingTitle: "To validate before publication",
      classifyAdmin: "Classify in admin",
      emptyGuest: "Sign in to see member-only documents. Visitors only see validated public content.",
      emptyMember:
        "No document visible for your profile — [DEMO] content or awaiting Hanshi validation.",
      emptyFiltered: "No document matches — try Reset.",
    },
    protocols: {
      title: "Protocols / grades / titles",
      subtitle: "Careful read-only mode — full publication subject to Hanshi validation.",
      warning:
        "Sensitive content: grades, titles and Black Dragon protocols remain visibility-controlled. Do not share publicly without Hanshi approval.",
      structureTitle: "Grades and titles (structure)",
      emptyGuest: "Sign in to access member-only protocols.",
      emptyMember:
        "No protocol published yet — sensitive content under Hanshi validation. See also the Documents section.",
    },
    privacy: {
      title: "Privacy Policy",
      subtitle: "Phase 1 demonstration platform — no real personal data processed yet.",
      metaDescription:
        "Minimal privacy policy — Shotokan AAMC Phase 1 portal in demonstration mode.",
      metaDescriptionEn:
        "Minimal privacy policy — Shotokan AAMC Phase 1 portal in demonstration mode.",
      contactLink: "Contact",
      footerNote:
        "Current version: demonstration and preparation platform. Final practices will be defined before any production use with real data.",
      sections: [
        {
          id: "demo",
          heading: "Demonstration data",
          bullets: [
            "Displayed data is marked [DEMO] or comes from received documents pending validation.",
            "No real personal data is automatically imported in Phase 1.",
          ],
        },
        {
          id: "no-real",
          heading: "What the system does not do yet",
          bullets: [
            "No payments are handled.",
            "No real documents are stored on a secure server.",
            "No real authentication backend is active.",
            "No sale or sharing of data with third parties.",
          ],
        },
        {
          id: "future",
          heading: "Future use",
          bullets: [
            "User accounts may be created in Phase 2, with consent and rules to be defined.",
            "Official contact coordinates will be confirmed by the organization.",
          ],
        },
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "Information and requests — Phase 1 / demonstration mode.",
      metaDescription: "Shotokan AAMC contact — demonstration form, coordinates to be confirmed.",
      metaDescriptionEn: "Shotokan AAMC contact — demonstration form, coordinates to be confirmed.",
      intro:
        "This page explains how to request information. Official organization coordinates are still to be confirmed.",
      emailLabel: "Indicative email:",
      demoEmail: "contact [DEMO]@shotokan-aamc.demo",
      phase1Note:
        "Account requests and contact are in Phase 1 mode: no real server send, no message storage.",
      formNotice: "Visual simulation form — no data is sent or stored.",
      nameLabel: "Name",
      emailFieldLabel: "Email",
      messageLabel: "Message",
      submitLabel: "Simulate send",
      simulationMessage:
        "[DEMO] Message simulated locally only — no real send, no storage. Use the account request to test the Phase 1 path.",
      accountRequestLink: "Account request",
      privacyLink: "Privacy Policy",
    },
    dojoDetail: {
      notFound: "Dojo not found",
      notFoundMessage: "This dojo does not exist or was removed.",
      backToList: "← Back to dojo list",
      subtitleDemo: "Dojo profile — [DEMO] location",
      subtitleOfficial: "Dojo profile — official data",
      infoTitle: "Information",
      id: "Identifier",
      phone: "Phone",
      activeStudents: "Active students",
      attachedStudents: "Attached students",
      noStudents: "No [DEMO] or official student for this dojo.",
      unassigned: "Unassigned",
    },
  },
};

export function usePublicCopy() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const copy = PUBLIC_COPY[locale];
  return { locale, copy, isEn: locale === "en" };
}

export { PUBLIC_COPY };
